import type { Fraction } from '@learnforge/demo-math/numeric';
import { createSvgEl } from '@learnforge/demo-render';

export interface FractionBarOptions {
  fraction: Fraction;
  maxDenominator: number;
  editable: boolean;
  showSubdivisions: boolean;
  highlightUnit: 'numerator' | 'denominator' | 'whole' | null;
  width: number;
  height: number;
  /** Draw the fraction value centred inside the bar. Default true. */
  showLabel?: boolean;
  onChange?(next: Fraction): void;
}

export interface FractionBarHandle {
  group: SVGGElement;
  update(opts: Partial<FractionBarOptions>): void;
  destroy(): void;
}

export function createFractionBar(
  parent: SVGGElement,
  initial: FractionBarOptions,
): FractionBarHandle {
  const group = createSvgEl('g', { class: 'arith-bar' });
  parent.appendChild(group);

  let opts = { ...initial };

  function render(): void {
    while (group.firstChild) group.removeChild(group.firstChild);
    const { fraction, width, height, showSubdivisions } = opts;
    const bg = createSvgEl('rect', {
      class: 'arith-bar-bg',
      x: 0,
      y: 0,
      width,
      height,
      rx: 4,
    });
    group.appendChild(bg);
    const den = fraction.den;
    const absNum = Math.abs(fraction.num);
    // One whole is always the FULL bar width (so "1" looks the same size always).
    // Values above 1 wrap onto stacked rows, grouped together; rows shrink in
    // height as the value grows so the whole group still fits.
    const wholes = Math.max(1, Math.ceil(absNum / den));
    const rowGap = wholes > 1 ? 3 : 0;
    const rowH = (height - (wholes - 1) * rowGap) / wholes;
    const cellW = width / den;
    if (showSubdivisions) {
      for (let w = 0; w < wholes; w += 1) {
        const rowY = w * (rowH + rowGap);
        for (let i = 0; i < den; i += 1) {
          const filled = w * den + i < absNum;
          const cls = filled ? 'arith-bar-fill arith-bar-subdivision' : 'arith-bar-subdivision';
          const cell = createSvgEl('rect', {
            class: cls,
            x: i * cellW,
            y: rowY,
            width: cellW,
            height: rowH,
          });
          group.appendChild(cell);
        }
      }
    }
    if (opts.showLabel !== false) {
      const label = createSvgEl('text', {
        class: 'arith-bar-label',
        x: width / 2,
        y: height / 2,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      });
      label.textContent = fraction.toString();
      group.appendChild(label);
    }
  }

  render();

  return {
    group,
    update(next: Partial<FractionBarOptions>) {
      opts = { ...opts, ...next };
      render();
    },
    destroy() {
      if (group.parentNode) group.parentNode.removeChild(group);
    },
  };
}
